import type AppView from "../../../../app.view";
import type { IFeatureFlagStatus } from "../../../../store/data/types";
import type { ICredentials } from "../../../../utils/credentialsStore";
import * as logger from "../../../../utils/logger";
import { FeatureFlagName } from "../../../../utils/featureFlagName";
import { createSha2Digest } from "../../../../utils/sha2Digest";
import { canReplaceNativeSession } from "../helpers/replaceNativeSession";
import type { IPasswordPolicy } from "../helpers/passwordRules";
import { showPasswordUpdatePrompt } from "../ui/components/PasswordUpdatePrompt/PasswordUpdatePrompt";
import { showUpdatePasswordPage } from "../ui/components/UpdatePasswordPage/UpdatePasswordPage";

// like MyGeotab, keyed on the session so a reload doesn't prompt again but a new login does
// only a hash is kept, since the session id is a credential
const getSkippedSessionKey = ({ database, userName }: ICredentials) => `passwordUpdatePromptSkippedSession:${ database }:${ userName }`;

const isSkippedForSession = (credentials: ICredentials, sessionHash: string): boolean => {
    try {
        return window.localStorage.getItem(getSkippedSessionKey(credentials)) === sessionHash;
    } catch {
        return false;
    }
};

const setSkippedForSession = (credentials: ICredentials, sessionHash: string): void => {
    window.localStorage.setItem(getSkippedSessionKey(credentials), sessionHash);
};

interface IPasswordUpdateSettings {
    passwordPolicy: IPasswordPolicy;
    disableDriveWeakPasswordUpdate?: boolean;
}

// co-drivers are always on the same database, so any driver's stored flag says whether the database has it on
// the server only sends the percentage when the flag is on for the database
const isPromptOnForDatabase = (app: AppView): boolean => {
    const flag = app.featureFlagService.getAllFeatureFlags().find(status => status.name === FeatureFlagName.PROMPT_USERS_WITH_WEAK_PASSWORDS_TO_UPDATE);
    return flag?.isEnabled === true || !!flag?.value;
};

// the flag is rolled out per driver and the stored flags can be another driver's, so it's read on this driver's session
const isPromptEnabled = async (app: AppView): Promise<boolean> => {
    const flags = await app.api.callPromise("GetFeatureFlagAuthenticated", { flags: [FeatureFlagName.PROMPT_USERS_WITH_WEAK_PASSWORDS_TO_UPDATE] }) as IFeatureFlagStatus[] | null;
    return flags?.[0]?.isEnabled === true;
};

const getPasswordUpdateSettings = async (app: AppView): Promise<IPasswordUpdateSettings> => {
    const [systemSettings] = await app.api.callPromise("Get", {
        typeName: "SystemSettings",
        propertySelector: { fields: ["passwordPolicy", "disableDriveWeakPasswordUpdate"], isIncluded: true }
    }) as IPasswordUpdateSettings[];
    return systemSettings;
};

/**
 * Prompts a basic auth driver whose password failed their database's password policy at login to update it.
 * Resolves once skipped or updated, or straight away when no prompt is needed. Never rejects, so login never stops on it.
 */
export async function promptPasswordUpdateIfRequired(app: AppView, credentials: ICredentials): Promise<void> {
    try {
        if (!credentials.sessionId || !canReplaceNativeSession()) {
            return;
        }

        const sessionHash = await createSha2Digest(credentials.sessionId);
        if (isSkippedForSession(credentials, sessionHash) || !isPromptOnForDatabase(app) || !(await isPromptEnabled(app))) {
            return;
        }

        // the policy is taken before prompting, so the driver is never offered a page that can't open
        const settings = await getPasswordUpdateSettings(app);
        if (settings.disableDriveWeakPasswordUpdate || !(await app.api.callPromise("IsPasswordUpdateRequired", {}))) {
            return;
        }

        if (!(await showPasswordUpdatePrompt(app))) {
            setSkippedForSession(credentials, sessionHash);
            return;
        }

        await showUpdatePasswordPage(app, settings.passwordPolicy);
    } catch (error) {
        logger.error(logger.Source.USER, `Password update check failed: ${ error?.message ?? String(error) }`, app.api.getUser());
    }
}
